import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { DISTRICTS } from '../data/mockData';
import { useApp } from '../context/AppContext';
import { ProductCard } from '../components/commerce/ProductCard';
import { StoreCard } from '../components/commerce/StoreCard';
import { EmptyState } from '../components/commerce/EmptyState';

export default function ExplorePage() {
  const { customMerchants } = useApp();
  const allMerchants = customMerchants;
  return (
    <div data-testid="explore-page" className="mx-auto max-w-[1280px] px-4 py-10">
      <h1 className="font-display text-3xl font-semibold text-kelate-darkwood sm:text-4xl">Explore Kelantan</h1>
      <p className="mt-1 text-sm text-kelate-soft">Discover what each district has to offer — food, crafts, services and more.</p>
      <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {DISTRICTS.map((d) => {
          const count = allMerchants.filter((m) => m.district === d).length;
          return (
            <Link
              key={d}
              to={`/explore/${encodeURIComponent(d.toLowerCase().replace(/\s+/g, '-'))}`}
              data-testid={`explore-${d.toLowerCase().replace(/\s+/g, '-')}`}
              className="group rounded-2xl bg-kelate-surface p-5 shadow-[0_1px_4px_rgba(81,68,55,0.08)] transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_28px_rgba(81,68,55,0.14)]"
            >
              <MapPin size={18} className="text-kelate-wood" />
              <div className="mt-2 text-sm font-semibold text-kelate-ink group-hover:text-kelate-darkwood">{d}</div>
              <div className="text-xs text-kelate-soft">{count} businesses</div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export function DistrictPage() {
  const { customMerchants, customProducts } = useApp();
  const allMerchants = customMerchants;
  const { district } = useParams();
  const name = DISTRICTS.find((d) => d.toLowerCase().replace(/\s+/g, '-') === district) || district;
  const merchants = allMerchants.filter((m) => m.district === name);
  const products = customProducts.filter((p) => p.district === name);
  const food = products.filter((p) => ['Local Food', 'Food & Beverage', 'Bakery', 'Cafe'].includes(p.category));

  return (
    <div data-testid="district-page" className="mx-auto max-w-[1280px] px-4 py-10">
      <nav className="text-xs text-kelate-soft"><Link to="/explore" className="hover:text-kelate-wood">Explore Kelantan</Link> / <span className="text-kelate-ink">{name}</span></nav>
      <h1 className="mt-2 font-display text-3xl font-semibold text-kelate-darkwood sm:text-4xl">{name}</h1>
      <p className="mt-1 text-sm text-kelate-soft">{merchants.length} businesses · {products.length} products</p>

      <h2 className="mt-8 mb-3 text-base md:text-lg font-semibold text-kelate-ink">Popular Stores</h2>
      {merchants.length ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {merchants.map((m) => <StoreCard key={m.id} merchant={m} />)}
        </div>
      ) : <EmptyState icon={MapPin} title="No stores yet" description={`KELATE is onboarding merchants in ${name}.`} />}

      {food.length > 0 && (
        <>
          <h2 className="mt-10 mb-3 text-base md:text-lg font-semibold text-kelate-ink">Food & Local Products</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {food.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </>
      )}
      {products.filter((p) => !food.includes(p)).length > 0 && (
        <>
          <h2 className="mt-10 mb-3 text-base md:text-lg font-semibold text-kelate-ink">Products & Services</h2>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {products.filter((p) => !food.includes(p)).map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </>
      )}
    </div>
  );
}
